import m, {type ComponentType} from '@bitstillery/mithril'
import {delay} from '@bitstillery/common/lib/utils'
import {logger} from '@bitstillery/common/app'
import {pending_loads} from '@bitstillery/common/lib/api_cache'

/**
 * Let a CMS page load its content out of sight, so it can be shown in one piece.
 *
 * A CMS page is a shell of `AsyncData` blocks: on a cold visit each one fetches on mount and redraws
 * itself when it lands, so the page arrives in five or six steps — 1495px, 1788px, 2282px, 3170px,
 * 3664px — with thirty images streaming in behind that. That cascade is the flicker. A repeat visit
 * shows none of it, because every source is still cached and answers synchronously, so the whole page
 * renders in a single pass.
 *
 * So reach that state before the page is on screen: mount it on a staging node, let its sources (and
 * images) load, throw the staging tree away, and only then let the real page render — from cache, in one
 * pass. What the visitor sees is the page they were on, and then the new page, complete.
 *
 * Two details carry the whole thing:
 *  - the staging node is IN the document (hidden, inert, out of flow). A detached node gets no styles,
 *    so background images would never be requested.
 *  - it is mounted with `m.mount`, not `m.render`, so it is a real redraw root. Without that the nested
 *    `AsyncData` (categories → media folders, brand holders → media folders) never mount, and precisely
 *    those would still pop in after the reveal.
 *
 * Warming costs nothing when there is nothing to warm: a page whose sources are all cached starts no
 * loads at all when it mounts, and is shown immediately.
 */

/**
 * Longest a click waits on the page it is leaving. Only reached when nothing warmed the page ahead of
 * time: this page's sources take ~380ms to settle from cold locally, so the cap has to sit above that
 * to be worth anything, and well under the point where a click reads as ignored.
 */
const SETTLE_DEADLINE = 600

/** Gap between checks. Also the window a nested `AsyncData` gets to mount and claim its own request. */
const SETTLE_POLL = 25

/** Two quiet checks in a row, so a nested source that starts right after its parent lands still counts. */
const QUIET_CHECKS = 2

/** Warms in progress, so the second caller for a page joins the first instead of staging it again. */
const warming = new Map<string, Promise<void>>()

function staging_node(): HTMLElement {
    const node = document.createElement('div')
    node.setAttribute('aria-hidden', 'true')
    node.setAttribute('inert', '')
    // Laid out (so styles resolve and images are fetched) but invisible, unreachable, and unable to
    // affect the page it is staged over.
    node.style.cssText =
        'position:fixed;top:0;left:0;width:100vw;height:100vh;visibility:hidden;pointer-events:none;z-index:-1;overflow:hidden;contain:strict'
    return node
}

async function stage(component: ComponentType): Promise<void> {
    const node = staging_node()
    document.body.appendChild(node)
    // Loads already running for the page on screen are not this page's to wait for; only growth above
    // this mark is. `pending_loads()` counts every cache, so the media folders behind the page's images
    // count too — they are half of what makes a cold page arrive in pieces.
    const baseline = pending_loads()
    try {
        // A wrapper, so the page's own component object is never registered as a mount root: mithril
        // keys mount roots by component, and the visible page may be rendering this very component
        // (hovering a link to the page you are on) — its targeted redraws would land in here instead.
        m.mount(node, {view: () => m(component)})
        if (pending_loads() <= baseline) {
            // Mounting started nothing: every source answered from cache, so the page is ready as it is.
            return
        }
        for (let quiet = 0, poll = 0; quiet < QUIET_CHECKS && poll < SETTLE_DEADLINE / SETTLE_POLL; poll++) {
            await delay(SETTLE_POLL, null)
            quiet = pending_loads() <= baseline ? quiet + 1 : 0
        }
    } catch (error) {
        logger.debug(`[cms warmup] staging render failed: ${error instanceof Error ? error.message : String(error)}`)
    } finally {
        try {
            m.mount(node, null)
        } catch {
            // The staging tree is discarded either way; a failing unmount must not block the navigation.
        }
        node.remove()
    }
}

/**
 * Mount `page_path`'s compiled component out of sight and resolve once its data has settled (or
 * `SETTLE_DEADLINE` passes). Best-effort: a page that fails to render staged is simply shown the way it
 * always was. Concurrent calls for the same page share one staging mount — a navigation regularly asks
 * for this twice (the route resolver, and `CmsDynamicPage` reacting to the same URL change).
 */
export function warm_cms_page(page_path: string, component: ComponentType): Promise<void> {
    if (globalThis.__SSR_MODE__) {
        return Promise.resolve()
    }
    let warm = warming.get(page_path)
    if (!warm) {
        warm = stage(component).finally(() => warming.delete(page_path))
        warming.set(page_path, warm)
    }
    return warm
}
